/**
 * Integration API (`/api/integrations/v1`) — the narrow, revocable REST surface
 * an external site's backend uses to submit build requests to one project
 * (project tokens) or to create prototypes (workspace tokens), poll their
 * status and file follow-up feedback. Distinct from MCP
 * (a personal, read-only AI-tool surface) and from prototype feedback ingest
 * (write-only). See docs/INTEGRATIONS.md.
 */

/** A token's kind: pinned to one project, or workspace-level (the prototype factory). */
export type IntegrationTokenKind = 'project' | 'workspace';

/**
 * What an integration token may do. `requests:*` and `feedback:write` belong to
 * PROJECT tokens (the `/requests…` and `/feedback` routes), `prototypes:*` to
 * WORKSPACE tokens (the `/prototypes…` routes) — a token only ever carries
 * scopes of its own kind. `feedback:write` is write-only: it files end-user
 * feedback and can read nothing, not even what it filed.
 */
export type IntegrationScope =
  | 'requests:write'
  | 'requests:read'
  | 'feedback:write'
  | 'prototypes:write'
  | 'prototypes:read';

/** The write-only end-user feedback scope (`POST /integrations/v1/feedback`). */
export const FEEDBACK_WRITE_SCOPE = 'feedback:write' satisfies IntegrationScope;

/** Name of the token behind a project's / prototype's "End-user feedback" card. */
export const END_USER_FEEDBACK_TOKEN_NAME = 'End-user feedback';

/** Tag on end-user feedback filed through `POST /feedback` (app requests carry `external`). */
export const END_USER_FEEDBACK_TAG = 'feedback';

/** Path of the end-user feedback route, below the API base (`AIAI_FEEDBACK_URL`). */
export const END_USER_FEEDBACK_PATH = '/api/integrations/v1/feedback';

/** Scopes a project token may carry. */
export const PROJECT_INTEGRATION_SCOPES: readonly IntegrationScope[] = [
  'requests:write',
  'requests:read',
  'feedback:write',
];
/** Scopes a workspace (prototype-factory) token may carry. */
export const WORKSPACE_INTEGRATION_SCOPES: readonly IntegrationScope[] = ['prototypes:write', 'prototypes:read'];
/** Every scope (request validation); the API then checks each fits the token's kind. */
export const INTEGRATION_SCOPES: readonly IntegrationScope[] = [
  ...PROJECT_INTEGRATION_SCOPES,
  ...WORKSPACE_INTEGRATION_SCOPES,
];

export function integrationScopesForKind(kind: IntegrationTokenKind): readonly IntegrationScope[] {
  return kind === 'workspace' ? WORKSPACE_INTEGRATION_SCOPES : PROJECT_INTEGRATION_SCOPES;
}

/**
 * Scopes a token gets when the mint names none (and the mint form's default
 * ticks): every scope of its kind except `feedback:write`, which the
 * "End-user feedback" card mints on its own.
 */
export function defaultIntegrationScopesForKind(kind: IntegrationTokenKind): readonly IntegrationScope[] {
  return integrationScopesForKind(kind).filter((s) => s !== FEEDBACK_WRITE_SCOPE);
}

/** The only token kind that may use a scope — so a route's required scope names the kind it serves. */
export function integrationScopeKind(scope: IntegrationScope): IntegrationTokenKind {
  return scope.startsWith('prototypes:') ? 'workspace' : 'project';
}

/** Integration API 403 `code`: the token lacks the route's scope (body also carries `requiredScope` when known). */
export const INSUFFICIENT_SCOPE_CODE = 'INSUFFICIENT_SCOPE';
/** Integration API 403 `code`: a project token on a `/prototypes` route, or a workspace token on a `/requests` route. */
export const WRONG_TOKEN_KIND_CODE = 'WRONG_TOKEN_KIND';

/** Origin label on every issue filed through the Integration API. */
export const INTEGRATION_ORIGIN_TAG = 'external';

/** Human-facing prefix so a leaked token is recognizable in logs/secret scanners. */
export const INTEGRATION_TOKEN_PREFIX = 'aiai_int_';

/** A token as listed on the owner's settings — never carries the secret or its hash. */
export interface IntegrationTokenDto {
  id: string;
  /** `project` = pinned to `projectId`; `workspace` = prototype-factory token (`projectId` null). */
  kind: IntegrationTokenKind;
  projectId: string | null;
  name: string;
  scopes: IntegrationScope[];
  createdAt: string;
  lastUsedAt: string | null;
  /** The status-change webhook URL, or null when none is set. */
  webhookUrl: string | null;
  /** A signing secret exists (its value is never returned after creation). */
  hasWebhookSecret: boolean;
}

/** The mint response — the ONLY place the plaintext token ever appears. */
export interface IntegrationTokenCreatedDto extends IntegrationTokenDto {
  token: string;
}

/**
 * The external-facing lifecycle of a request (an issue on the token's
 * project), deliberately coarser than the internal issue state:
 *  - received          filed, the Product Manager has not picked it up yet
 *  - awaiting_approval planned, but the project is paused — a human must approve
 *  - planning          the Product Manager is working on it
 *  - building          released to the Engineer (queued, running, PR open/merging)
 *  - retrying          a build attempt failed and the platform is retrying it
 *  - done              fixed, and (for a prototype) the fix is live
 *  - failed            the build retry budget is exhausted
 *  - dismissed         closed as won't do / duplicate
 */
export type IntegrationRequestStatus =
  | 'received'
  | 'awaiting_approval'
  | 'planning'
  | 'building'
  | 'retrying'
  | 'done'
  | 'failed'
  | 'dismissed';

/** `POST /requests` and `POST /requests/:id/feedback` response. */
export interface IntegrationRequestCreatedDto {
  id: string;
  shortCode: string | null;
  state: string;
  externalRef: string | null;
}

/** `GET /requests/:id` response. */
export interface IntegrationRequestDto {
  id: string;
  shortCode: string | null;
  externalRef: string | null;
  status: IntegrationRequestStatus;
  prUrl: string | null;
  previewUrl: string | null;
  deployedSha: string | null;
  live: boolean;
  updatedAt: string;
}

// ── Prototype factory (workspace tokens) ────────────────────────────────────

/** `initial` = the brief the prototype was created from; `feedback` = filed later via the API. */
export type IntegrationPrototypeRequestKind = 'initial' | 'feedback';

/** One request on a factory prototype, with its own mapped status. */
export interface IntegrationPrototypeRequestDto {
  id: string;
  shortCode: string | null;
  externalRef: string | null;
  status: IntegrationRequestStatus;
  live: boolean;
  kind: IntegrationPrototypeRequestKind;
}

/**
 * `POST /prototypes`, `GET /prototypes/:id`, `POST /prototypes/:id/feedback`
 * response — and the `prototype.status_changed` webhook body. `status`/`live`
 * are those of the initial request; `previewUrl` is known immediately and
 * serves once the first deploy finishes; `deployStatus` is the raw deployment
 * status. Never carries run logs, secrets or the repo URL.
 */
export interface IntegrationPrototypeDto {
  id: string;
  externalRef: string | null;
  title: string;
  /** The catalog design style: the one sent on create, else the one the platform picked (null = none). */
  designId?: string | null;
  status: IntegrationRequestStatus;
  live: boolean;
  previewUrl: string | null;
  deployStatus: string | null;
  /** The initial request, then every feedback request, oldest first. */
  requests: IntegrationPrototypeRequestDto[];
  /** The agents' autonomous work on the prototype, across every issue source. */
  iteration: IntegrationPrototypeIterationDto;
  /** Shipped work (live on `previewUrl`), newest first, at most 50. */
  changes: IntegrationPrototypeChangeDto[];
  /**
   * Work on its way to `previewUrl` after v1 (the brief itself is never
   * listed), oldest-planned first (newest-planned last), at most 10. Only
   * items with a visitor-facing title in the prototype's locale are listed;
   * the rest still count in `iteration.planned` / `iteration.building`.
   */
  upcoming: IntegrationPrototypeUpcomingDto[];
  /** First-build progress of the initial request; null once it is live. */
  progress?: IntegrationPrototypeProgressDto | null;
  /** The app's own admin area and its one-time claim code; null for a static prototype. */
  admin?: IntegrationPrototypeAdminDto | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * A full-stack factory prototype's admin area. The first person to open `url`
 * creates the app's owner login, and must enter `setupCode` to do so (email
 * it with the live link). `setupRequired` is the live app's own first-run
 * status, re-checked every few minutes: true = nobody has claimed it yet,
 * false = the owner account exists, null = not known yet (not live / unreachable).
 */
export interface IntegrationPrototypeAdminDto {
  url: string;
  setupRequired: boolean | null;
  setupCode: string | null;
}

/**
 * Visitor-safe phases of a prototype's FIRST build, in lifecycle order:
 * `planning → queued → building → testing → deploying` (then `progress: null`
 * once live). `retrying` = an attempt failed and another is queued or running;
 * `failed` = the platform gave up.
 */
export const INTEGRATION_PROTOTYPE_PROGRESS_PHASES = [
  'planning',
  'queued',
  'building',
  'testing',
  'deploying',
  'retrying',
  'failed',
] as const;
export type IntegrationPrototypeProgressPhase = (typeof INTEGRATION_PROTOTYPE_PROGRESS_PHASES)[number];

/**
 * Coarse first-build progress. `step` (1..`steps`) and `percent` never go
 * backwards, not even across a retry. `estimatedReadyAt` is a rough estimate
 * (median of recent first builds), null once failed.
 */
export interface IntegrationPrototypeProgressDto {
  phase: IntegrationPrototypeProgressPhase;
  step: number;
  steps: number;
  percent: number;
  startedAt: string;
  estimatedReadyAt: string | null;
}

/**
 * Counts over EVERY issue on a factory prototype (brief, API feedback, agent
 * follow-ups). `active` stays true until 30 quiet minutes have passed with no
 * planned or building work; `settledAt` is when it turned false (null while
 * active and before the initial build is live).
 */
export interface IntegrationPrototypeIterationDto {
  active: boolean;
  planned: number;
  building: number;
  shipped: number;
  failed: number;
  lastShippedAt: string | null;
  settledAt: string | null;
}

/** `initial` = the brief; `feedback` = API feedback; `fix` = a bug fix; `improvement` = anything else. */
export type IntegrationPrototypeChangeKind = 'initial' | 'feedback' | 'improvement' | 'fix';

/** One shipped change, with a plain-language, visitor-facing `title`. */
export interface IntegrationPrototypeChangeDto {
  id: string;
  shortCode: string | null;
  kind: IntegrationPrototypeChangeKind;
  title: string;
  shippedAt: string;
}

/** `planned` = queued for the agents; `building` = being built, or merged and not live yet. */
export type IntegrationPrototypeUpcomingState = 'planned' | 'building';

/** One item on its way to the visitor, with a plain-language, visitor-facing `title`. */
export interface IntegrationPrototypeUpcomingDto {
  id: string;
  shortCode: string | null;
  kind: Exclude<IntegrationPrototypeChangeKind, 'initial'>;
  title: string;
  state: IntegrationPrototypeUpcomingState;
}

/** Locales `POST /prototypes` accepts for the visitor-facing change titles. */
export const INTEGRATION_PROTOTYPE_LOCALES = ['nl', 'en'] as const;
export type IntegrationPrototypeLocale = (typeof INTEGRATION_PROTOTYPE_LOCALES)[number];

/**
 * 400 `code` when `POST /prototypes` refuses a brief the inbound screen judged
 * spam / abuse / empty (body also carries `verdict`). Nothing was created.
 */
export const BRIEF_REJECTED_CODE = 'BRIEF_REJECTED';

// ── Webhooks (phase 2) ──────────────────────────────────────────────────────

/** Header carrying `sha256=<hex HMAC-SHA256 of the raw request body>`. */
export const INTEGRATION_WEBHOOK_SIGNATURE_HEADER = 'X-Aiai-Signature';
/** Header carrying the send time in unix seconds. */
export const INTEGRATION_WEBHOOK_TIMESTAMP_HEADER = 'X-Aiai-Timestamp';
/** Header carrying the delivery id — the same on every retry of one delivery. */
export const INTEGRATION_WEBHOOK_DELIVERY_HEADER = 'X-Aiai-Delivery';
/** Header naming the event: `request.status_changed` (project tokens) or `prototype.status_changed` (workspace tokens). */
export const INTEGRATION_WEBHOOK_EVENT_HEADER = 'X-Aiai-Event';
export const INTEGRATION_WEBHOOK_EVENT = 'request.status_changed';
export const INTEGRATION_PROTOTYPE_WEBHOOK_EVENT = 'prototype.status_changed';
export type IntegrationWebhookEvent =
  | typeof INTEGRATION_WEBHOOK_EVENT
  | typeof INTEGRATION_PROTOTYPE_WEBHOOK_EVENT;
/** Human-facing prefix of a webhook signing secret. */
export const INTEGRATION_WEBHOOK_SECRET_PREFIX = 'whsec_';

/**
 * A token's webhook, as returned by set / rotate. `secret` is the plaintext
 * signing secret — present ONLY when it was just generated (first set or
 * rotate), never again.
 */
export interface IntegrationWebhookDto {
  tokenId: string;
  webhookUrl: string | null;
  hasWebhookSecret: boolean;
  secret: string | null;
}

/** Outcome of one delivery attempt. */
export type IntegrationWebhookDeliveryStatus = 'delivered' | 'retrying' | 'failed' | 'skipped';

/** One row of a token's delivery log — never the payload or the secret. */
export interface IntegrationWebhookDeliveryDto {
  id: string;
  deliveryId: string;
  /** The request the delivery was about (project tokens); null for a prototype delivery. */
  issueId: string | null;
  /** The prototype the delivery was about (workspace tokens); null for a request delivery. */
  prototypeId: string | null;
  /** The request's short code, or the prototype's issue-code prefix. */
  shortCode: string | null;
  /** The request (or prototype) status the payload carried. */
  requestStatus: string;
  status: IntegrationWebhookDeliveryStatus;
  attempt: number;
  httpStatus: number | null;
  error: string | null;
  createdAt: string;
}
